import { useState, useEffect, useRef } from 'react';
import { CanonicalWine, CanonicalWineSearchResult } from '../types';
import { supabase } from '../lib/supabase';

interface WineSearchScreenProps {
  onSelectWine: (wine: CanonicalWine) => void;
  onCreateNew: (searchTerm: string) => void;
  onScan: () => void;
  onClose: () => void;
}

export function WineSearchScreen({ onSelectWine, onCreateNew, onScan, onClose }: WineSearchScreenProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<CanonicalWineSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (searchTerm.length < 2) {
      setResults([]);
      setHasSearched(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      setHasSearched(true);
      
      try {
        const { data: fuzzyData, error: fuzzyError } = await supabase
          .rpc('search_canonical_wines', { 
            search_query: searchTerm,
            limit_count: 10 
          });

        if (!fuzzyError && fuzzyData && fuzzyData.length > 0) {
          setResults(fuzzyData);
        } else {
          const { data, error } = await supabase
            .from('canonical_wines')
            .select('*')
            .or(`wine_name.ilike.%${searchTerm}%,producer.ilike.%${searchTerm}%`)
            .limit(10);

          if (error) throw error;
          setResults((data || []).map(w => ({ ...w, similarity_score: 0 })));
        }
      } catch (error) {
        console.error('Search error:', error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchTerm]);

  const getColorClass = (color?: string) => {
    switch (color) {
      case 'red': return 'wine-color-red';
      case 'white': return 'wine-color-white';
      case 'rosé': return 'wine-color-rose';
      case 'orange': return 'wine-color-orange';
      default: return 'wine-color-red';
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div 
        className="ios-sheet w-full"
        style={{ height: '90vh', display: 'flex', flexDirection: 'column' }}
        onClick={e => e.stopPropagation()}
      >
        <div className="ios-sheet-handle" />

        {/* Header */}
        <div style={{ 
          padding: '0 20px 12px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--color-wine)',
              fontSize: '16px',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <h2 className="wine-title" style={{ 
            fontSize: '18px', 
            margin: 0,
            color: 'var(--color-text)'
          }}>
            Add Wine
          </h2>
          <div style={{ width: '50px' }} />
        </div>

        {/* Search Input */}
        <div style={{ padding: '0 20px 12px' }}>
          <div style={{ position: 'relative' }}>
            <span style={{ 
              position: 'absolute', 
              left: '12px', 
              top: '50%', 
              transform: 'translateY(-50%)',
              fontSize: '16px',
              opacity: 0.5
            }}>🔍</span>
            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search wines..."
              className="ios-input"
              style={{ paddingLeft: '40px' }}
            />
          </div>
        </div>

        {/* Scan Button */}
        <div style={{ padding: '0 20px 16px' }}>
          <button
            onClick={onScan}
            className="ios-btn w-full"
            style={{ 
              background: 'linear-gradient(135deg, var(--color-wine) 0%, var(--color-wine-light) 100%)',
              color: 'white'
            }}
          >
            📷 Scan Wine Label
          </button>
        </div>

        {/* Results */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading && (
            <div style={{ padding: '32px', textAlign: 'center', color: 'var(--color-text-muted)' }}>
              Searching...
            </div>
          )}

          {!loading && results.length > 0 && (
            <div className="ios-card" style={{ margin: '0 16px', overflow: 'hidden' }}>
              {results.map((wine, index) => (
                <div
                  key={wine.id}
                  className="ios-list-row"
                  onClick={() => onSelectWine(wine)}
                  style={{ 
                    cursor: 'pointer',
                    borderBottom: index < results.length - 1 ? '1px solid var(--color-border)' : 'none'
                  }}
                >
                  <div className={`wine-color-dot ${getColorClass(wine.color)}`} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                      <span className="wine-title" style={{ 
                        fontSize: '16px', 
                        color: 'var(--color-text)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {wine.wine_name}
                      </span>
                      {wine.vintage && (
                        <span style={{ fontSize: '14px', color: 'var(--color-text-muted)', flexShrink: 0 }}>
                          {wine.vintage}
                        </span>
                      )}
                    </div>
                    <div style={{ 
                      fontSize: '13px', 
                      color: 'var(--color-text-muted)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {wine.producer}
                      {wine.varietal && ` · ${wine.varietal}`}
                    </div>
                  </div>
                  <span style={{ fontSize: '18px', color: 'var(--color-border)' }}>›</span>
                </div>
              ))}
            </div>
          )}

          {!loading && hasSearched && results.length === 0 && searchTerm.length >= 2 && (
            <div style={{ padding: '32px', textAlign: 'center' }}>
              <p style={{ color: 'var(--color-text-muted)', marginBottom: '8px' }}>
                No wines found for "{searchTerm}"
              </p>
            </div>
          )}

          {!loading && searchTerm.length < 2 && (
            <div style={{ padding: '32px', textAlign: 'center', color: 'var(--color-text-muted)' }}>
              <p>Type to search our wine database</p>
              <p style={{ fontSize: '14px', marginTop: '8px' }}>or scan a label to auto-fill</p>
            </div>
          )}
        </div>

        {/* Add New Wine Button */}
        <div style={{ 
          padding: '16px 20px', 
          borderTop: '1px solid var(--color-border)',
          background: 'var(--color-card)'
        }}>
          <button
            onClick={() => onCreateNew(searchTerm)}
            className="ios-btn ios-btn-secondary w-full"
          >
            + Add Wine Manually {searchTerm && `"${searchTerm}"`}
          </button>
        </div>
      </div>
    </div>
  );
}
